import { z } from 'zod';

/** A folder in the tenant's file-manager tree. */
export interface Folder {
  id: string;
  parentId: string | null;
  name: string;
  createdAt: string;
  updatedAt: string;
}

export const FolderCreateSchema = z.object({
  name: z.string().min(1).max(200),
  parentId: z.string().uuid().nullable().optional(),
});
export type FolderCreate = z.infer<typeof FolderCreateSchema>;

export const FolderUpdateSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  parentId: z.string().uuid().nullable().optional(),
});
export type FolderUpdate = z.infer<typeof FolderUpdateSchema>;

/**
 * A file as seen in the manager = an attachment plus its folder and the record
 * it belongs to (`entityType`/`entityId`). `entityType === 'file'` means a
 * standalone file uploaded into the manager (not tied to a record).
 */
export interface FileItem {
  id: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  folderId: string | null;
  entityType: string;
  entityId: string;
  uploadedAt: string;
}

export interface FilesList {
  items: FileItem[];
  /** How many files match the filter — `items` is one page of them. */
  total: number;
}

/**
 * Sidebar counters + storage breakdown, aggregated server-side. The manager
 * shows these for the whole tenant while listing only a page of files, so they
 * cannot be derived from the page it holds.
 */
export interface FileStats {
  totalCount: number;
  totalBytes: number;
  unfiledCount: number;
  /** folder id → file count. */
  byFolder: Record<string, number>;
  /** entity_type → count + bytes. */
  bySource: Record<string, { count: number; bytes: number }>;
}

/** Move/unfile a file: `folderId: null` returns it to "Unfiled". */
export const FileMoveSchema = z.object({ folderId: z.string().uuid().nullable() });
export type FileMove = z.infer<typeof FileMoveSchema>;
